import { useContext, useRef } from 'react'
import {
  RadioGroup as AriaRadioGroup,
  RadioGroupStateContext,
} from 'react-aria-components'
import {
  useRadio,
  type AriaRadioProps,
  type AriaRadioGroupProps,
} from 'react-aria'
import cx from 'classnames'
import { useIsSkeleton } from '@/components/Skeleton'
import {
  SelectionLabel,
  SelectionLabelControl,
  SelectionLabelText,
} from './SelectionLabel'
import styles from './RadioGroup.module.css'
import inputStyles from './Radio.module.css'
import { RadioIndicator, type RadioIndicatorProps } from './Radio'

interface RadioOptionProps
  extends AriaRadioProps, Omit<RadioIndicatorProps, 'children'> {
  label: string
  testId?: string
}

function RadioOption({
  value,
  label,
  isSkeleton,
  variant,
  isPadded,
  testId,
  ...rest
}: RadioOptionProps) {
  const state = useContext(RadioGroupStateContext)

  if (state == null) {
    throw new Error('RadioOption must be used inside a RadioGroup')
  }

  const ref = useRef<HTMLInputElement>(null)
  const { labelProps, inputProps, isDisabled, isSelected } = useRadio(
    { value, ...rest },
    state,
    ref,
  )

  return (
    <SelectionLabel {...labelProps}>
      <SelectionLabelControl>
        <RadioIndicator
          disabled={isDisabled}
          isSkeleton={isSkeleton}
          isPadded={isPadded}
          variant={variant}
        >
          <input
            {...inputProps}
            checked={isSelected}
            disabled={isDisabled}
            ref={ref}
            className={inputStyles.input}
            data-test={testId}
          />
        </RadioIndicator>
      </SelectionLabelControl>
      <SelectionLabelText isSkeleton={isSkeleton}>{label}</SelectionLabelText>
    </SelectionLabel>
  )
}

export type Key = string

export interface RadioGroupProps<T extends object>
  extends
    Omit<AriaRadioGroupProps, 'children'>,
    Omit<RadioIndicatorProps, 'children' | 'disabled'> {
  /**
   * Select items.
   */
  items: T[]
  /**
   * Get the key for an item.
   */
  getKey: (item: T) => Key
  /**
   * Get the label for an item.
   */
  getLabel: (item: T) => string
  /**
   * Keys that should be disabled.
   */
  disabledKeys?: Set<Key>
  /**
   * Test ID attribute.
   */
  testId?: string
}

export function RadioGroup<T extends object>({
  items,
  getKey,
  getLabel,
  disabledKeys,
  isSkeleton,
  isPadded,
  variant,
  testId,
  orientation = 'vertical',
  isDisabled,
  ...rest
}: RadioGroupProps<T>) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <AriaRadioGroup
      {...rest}
      orientation={orientation}
      isDisabled={isDisabled || isSkeleton}
      className={cx(
        styles.radioGroup,
        orientation === 'vertical' ? styles.vertical : styles.horizontal,
      )}
      data-test={testId}
    >
      {items.map((item) => {
        const key = getKey(item)
        return (
          <RadioOption
            key={key}
            value={key}
            aria-label={getLabel(item)}
            label={getLabel(item)}
            isDisabled={isDisabled || isSkeleton || disabledKeys?.has(key)}
            isSkeleton={isSkeleton}
            isPadded={isPadded}
            variant={variant}
            testId={testId ? `${testId}-option-${key}` : undefined}
          />
        )
      })}
    </AriaRadioGroup>
  )
}
